import type {
  ApiRequestError,
  VideoSession,
  SessionWithRounds,
  SessionListResponse,
  RoundListResponse,
  VideoRound,
  CreateRoundRequest,
} from '../types'
import { trackedFetch } from '../util/apiReport'

const API_BASE = 'https://docer-api.wps.cn/ai-docer/aivideo/api'

let _sessReqCounter = 0
function genReqId(): string {
  _sessReqCounter++
  const ts = Date.now().toString(36)
  const rnd = Math.random().toString(36).substring(2, 6)
  return `${ts}-${rnd}-${_sessReqCounter}`
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers)
  headers.set('Content-Type', 'application/json')
  const url = new URL(`${API_BASE}${path}`)
  url.searchParams.set('x-request-id', genReqId())
  const res = await trackedFetch(url.toString(), {
    ...options,
    credentials: 'include',
    headers,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string; message?: string }
    const err = new Error(body.message || body.error || `HTTP ${res.status}`) as ApiRequestError
    err.serverError = body.message || body.error
    err.errorCode = body.error || ''
    throw err
  }
  return res.json() as Promise<T>
}

export async function listSessions(cursor?: string, status?: string): Promise<VideoSession[]> {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  if (status) params.set('status', status)
  const qs = params.toString()
  const data = await request<SessionListResponse>(`/sessions${qs ? '?' + qs : ''}`)
  return data.sessions
}

export async function createSession(title?: string): Promise<VideoSession> {
  return request<VideoSession>('/sessions', {
    method: 'POST',
    body: JSON.stringify({ title: title || '' }),
  })
}

export async function getSession(id: string): Promise<SessionWithRounds> {
  return request<SessionWithRounds>(`/sessions/${id}`)
}

export async function updateSession(
  id: string,
  updates: { title?: string; status?: string }
): Promise<VideoSession> {
  return request<VideoSession>(`/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

export async function deleteSession(id: string): Promise<void> {
  await request<{ ok: string }>(`/sessions/${id}`, { method: 'DELETE' })
}

export async function listRounds(sessionId: string): Promise<VideoRound[]> {
  const data = await request<RoundListResponse>(`/sessions/${sessionId}/rounds`)
  return data.rounds
}

export async function createRound(sessionId: string, body: CreateRoundRequest): Promise<VideoRound> {
  return request<VideoRound>(`/sessions/${sessionId}/rounds`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
